import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Card,
    CardContent,
    CardActions,
    CircularProgress,
    Alert,
    Chip,
    Grid,
    Tabs,
    Tab,
    LinearProgress,
} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    PlayArrow as StartIcon,
    AccessTime as TimeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { userQuestionnaireService, IUserQuestionnaireAssignment, IQuestionnaire } from '../services/questionnaireService';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

const AssignedQuestionnaires: React.FC = () => {
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);

    // Fetch all assignments
    const {
        data: assignments = [],
        isLoading,
        error
    } = useQuery<IUserQuestionnaireAssignment[]>({
        queryKey: ['myQuestionnaireAssignments'],
        queryFn: () => userQuestionnaireService.getMyAssignments()
    });

    const pendingAssignments = assignments.filter(a => a.status === 'pending' || a.status === 'in-progress');
    const completedAssignments = assignments.filter(a => a.status === 'completed');

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'in-progress':
                return 'warning';
            default:
                return 'info';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed':
                return 'Completed';
            case 'in-progress':
                return 'In Progress';
            default:
                return 'Pending';
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleStartQuestionnaire = (assignmentId: string) => {
        navigate(`/assigned-questionnaires/${assignmentId}`);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Failed to load your questionnaires</Alert>
            </Box>
        );
    }

    const renderAssignmentCard = (assignment: IUserQuestionnaireAssignment) => {
        const questionnaire = assignment.questionnaireId as IQuestionnaire;
        if (!questionnaire) return null;

        return (
            <Grid item xs={12} md={6} lg={4} key={assignment._id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {questionnaire.title}
                            </Typography>
                            <Chip
                                label={getStatusLabel(assignment.status)}
                                size="small"
                                color={getStatusColor(assignment.status) as 'success' | 'warning' | 'info'}
                            />
                        </Box>

                        {questionnaire.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {questionnaire.description}
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <TimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                Assigned: {formatDate(assignment.assignedAt)}
                            </Typography>
                        </Box>

                        {assignment.completedAt && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <CheckCircleIcon fontSize="small" color="success" />
                                <Typography variant="body2" color="text.secondary">
                                    Completed: {formatDate(assignment.completedAt)}
                                </Typography>
                            </Box>
                        )}

                        <Typography variant="body2" sx={{ mt: 2 }}>
                            <strong>{questionnaire.steps?.length || 0}</strong> step(s) to complete
                        </Typography>

                        {assignment.status === 'in-progress' && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Progress
                                </Typography>
                                <LinearProgress variant="determinate" value={50} sx={{ borderRadius: 1 }} />
                            </Box>
                        )}
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                        {assignment.status === 'completed' ? (
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<AssignmentIcon />}
                                onClick={() => handleStartQuestionnaire(assignment._id)}
                            >
                                View Responses
                            </Button>
                        ) : (
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={assignment.status === 'pending' ? <StartIcon /> : <AssignmentIcon />}
                                onClick={() => handleStartQuestionnaire(assignment._id)}
                                sx={{ bgcolor: '#667eea' }}
                            >
                                {assignment.status === 'pending' ? 'Start Questionnaire' : 'Continue'}
                            </Button>
                        )}
                    </CardActions>
                </Card>
            </Grid>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    My Questionnaires
                </Typography>
                {pendingAssignments.length > 0 && (
                    <Chip
                        label={`${pendingAssignments.length} pending`}
                        color="warning"
                    />
                )}
            </Box>

            {assignments.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No questionnaires assigned
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        When a lawyer assigns you a questionnaire, it will appear here.
                    </Typography>
                </Paper>
            ) : (
                <>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
                            <Tab label={`Pending (${pendingAssignments.length})`} />
                            <Tab label={`Completed (${completedAssignments.length})`} />
                        </Tabs>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        {pendingAssignments.length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    All caught up!
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    You have no pending questionnaires.
                                </Typography>
                            </Paper>
                        ) : (
                            <Grid container spacing={3}>
                                {pendingAssignments.map(renderAssignmentCard)}
                            </Grid>
                        )}
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        {completedAssignments.length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    No completed questionnaires yet.
                                </Typography>
                            </Paper>
                        ) : (
                            <Grid container spacing={3}>
                                {completedAssignments.map(renderAssignmentCard)}
                            </Grid>
                        )}
                    </TabPanel>
                </>
            )}
        </Box>
    );
};

export default AssignedQuestionnaires;
